/*
 * klump-checkout.js — GetFit "Buy Now, Pay Later with Klump" for the whole cart.
 *
 * Flow (see the klump-backend service):
 *   1. Shopper edits the cart, clicks a .klump-buy-btn.
 *   2. We collect contact + delivery address in a modal.
 *   3. We fetch the store's real shipping rates (GIG/GUO/DHL) for that address
 *      and let the shopper pick a courier.
 *   4. POST the cart + chosen courier to the backend, which creates a Shopify
 *      draft order (re-pricing shipping server-side) and returns the
 *      authoritative { reference, amount }.
 *   5. new Klump({...}) opens the BNPL popup for that exact amount.
 *   6. On success we poll the backend until its webhook has created the paid
 *      Shopify order, then send the shopper to the real order-status page.
 *
 * Settings come from window.KLUMP_SETTINGS (injected by snippets/klump-loader).
 * The Klump SDK (global `Klump`) is loaded by that same snippet.
 */
(function () {
  'use strict';

  var S = window.KLUMP_SETTINGS || {};
  var BACKEND = String(S.backendUrl || '').replace(/\/+$/, '');
  var MIN_AMOUNT = Number(S.minAmount || 0); // naira; 0 = no minimum
  var COLLECT_ADDRESS = S.collectAddress !== false;

  // Shopify matches shipping zones by the 2-letter province CODE, not the name.
  // [code, name] for all Nigerian states.
  var NG_STATES = [
    ['AB', 'Abia'], ['AD', 'Adamawa'], ['AK', 'Akwa Ibom'], ['AN', 'Anambra'],
    ['BA', 'Bauchi'], ['BY', 'Bayelsa'], ['BE', 'Benue'], ['BO', 'Borno'],
    ['CR', 'Cross River'], ['DE', 'Delta'], ['EB', 'Ebonyi'], ['ED', 'Edo'],
    ['EK', 'Ekiti'], ['EN', 'Enugu'], ['FC', 'Federal Capital Territory (Abuja)'],
    ['GO', 'Gombe'], ['IM', 'Imo'], ['JI', 'Jigawa'], ['KD', 'Kaduna'],
    ['KN', 'Kano'], ['KT', 'Katsina'], ['KE', 'Kebbi'], ['KO', 'Kogi'],
    ['KW', 'Kwara'], ['LA', 'Lagos'], ['NA', 'Nasarawa'], ['NI', 'Niger'],
    ['OG', 'Ogun'], ['ON', 'Ondo'], ['OS', 'Osun'], ['OY', 'Oyo'],
    ['PL', 'Plateau'], ['RI', 'Rivers'], ['SO', 'Sokoto'], ['TA', 'Taraba'],
    ['YO', 'Yobe'], ['ZA', 'Zamfara']
  ];

  var cachedPublicKey = S.publicKey || null;
  var modal = null;
  var busy = false;

  // ---- helpers ------------------------------------------------------------
  function fmt(naira) {
    return '₦' + Number(naira || 0).toLocaleString('en-NG');
  }

  function toast(msg, isError) {
    var t = document.createElement('div');
    t.className = 'klump-toast' + (isError ? ' klump-toast--error' : '');
    t.textContent = msg;
    document.body.appendChild(t);
    // eslint-disable-next-line no-unused-expressions
    t.offsetHeight;
    t.classList.add('is-visible');
    setTimeout(function () {
      t.classList.remove('is-visible');
      setTimeout(function () { t.remove(); }, 300);
    }, 3200);
  }

  // Normalise a Nigerian phone number to local 0XXXXXXXXXX format. Shopify's
  // draft order (and Klump) reject "+234…" here, but the local format works.
  function normalizePhone(p) {
    if (!p) return p;
    var s = String(p).replace(/[\s()\-.]/g, '');
    if (s.charAt(0) === '+') s = s.slice(1); // drop leading +
    if (s.indexOf('234') === 0) s = '0' + s.slice(3); // country code -> local
    s = s.replace(/^0+/, '0'); // collapse accidental leading zeros (e.g. +2340803…)
    return s;
  }

  // Klump requires image_url to be an absolute URL. Shopify image fields can be
  // protocol-relative (//cdn.shopify.com/…) or root-relative, which Klump rejects.
  function toAbsoluteUrl(u) {
    if (!u) return undefined;
    u = String(u);
    if (u.indexOf('//') === 0) return 'https:' + u;
    if (/^https?:\/\//i.test(u)) return u;
    if (u.charAt(0) === '/') return window.location.origin + u;
    return undefined;
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  // Build a one-item "cart" from the product page's currently selected variant
  // + quantity, so a product-page Klump button pays for just that item.
  function getProductSelection(btn) {
    var handle = btn.getAttribute('data-product-handle');
    var vid = btn.getAttribute('data-variant-id');
    var qty = 1;
    // Prefer the live selection from the add-to-cart form (options/qty may have changed).
    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      var idInput = form.querySelector('[name="id"]');
      if (idInput && idInput.value) vid = idInput.value;
      var qInput = form.querySelector('[name="quantity"]');
      if (qInput && qInput.value) qty = Math.max(1, parseInt(qInput.value, 10) || 1);
    }
    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var variant = null;
        for (var i = 0; i < p.variants.length; i++) {
          if (String(p.variants[i].id) === String(vid)) { variant = p.variants[i]; break; }
        }
        if (!variant) variant = p.variants[0];
        var img = (variant.featured_image && variant.featured_image.src) || p.featured_image || '';
        var vt = variant.public_title || variant.title || '';
        return {
          item_count: qty,
          total_price: variant.price * qty, // kobo, same units as /cart.js
          items: [{
            variant_id: variant.id,
            quantity: qty,
            product_title: p.title,
            variant_title: vt,
            url: '/products/' + p.handle,
            image: img,
            final_price: variant.price
          }]
        };
      });
  }

  function getPublicKey() {
    if (cachedPublicKey) return Promise.resolve(cachedPublicKey);
    return fetch(BACKEND + '/api/klump/config')
      .then(function (r) { return r.json(); })
      .then(function (j) { cachedPublicKey = j.publicKey; return cachedPublicKey; })
      .catch(function () { return S.publicKey || null; });
  }

  // The Klump SDK requires a #klump__checkout element to exist before init.
  function ensureCheckoutHost() {
    var el = document.getElementById('klump__checkout');
    if (!el) {
      el = document.createElement('div');
      el.id = 'klump__checkout';
      el.style.display = 'none';
      document.body.appendChild(el);
    }
    return el;
  }

  // ---- modal --------------------------------------------------------------
  function buildModal() {
    if (modal) return modal;

    var overlay = document.createElement('div');
    overlay.className = 'klump-modal';
    overlay.setAttribute('aria-hidden', 'true');

    var stateOptions = NG_STATES.map(function (s) {
      return '<option value="' + s[0] + '">' + s[1] + '</option>';
    }).join('');

    var addressFields = COLLECT_ADDRESS ? (
      '<label class="klump-field klump-field--full"><span>Delivery address</span>' +
      '<input name="address1" type="text" autocomplete="address-line1" required></label>' +
      '<label class="klump-field klump-field--full"><span>Apartment, suite (optional)</span>' +
      '<input name="address2" type="text" autocomplete="address-line2"></label>' +
      '<label class="klump-field"><span>City</span>' +
      '<input name="city" type="text" autocomplete="address-level2" required></label>' +
      '<label class="klump-field"><span>State</span>' +
      '<select name="province" required><option value="" disabled selected>Select state</option>' +
      stateOptions + '</select></label>'
    ) : '';

    var deliverySection = COLLECT_ADDRESS ? (
      '<div class="klump-delivery">' +
        '<button type="button" class="klump-rates-btn">Get delivery options</button>' +
        '<div class="klump-rates" hidden></div>' +
      '</div>'
    ) : '';

    overlay.innerHTML =
      '<div class="klump-modal__dialog" role="dialog" aria-modal="true" aria-label="Pay with Klump">' +
        '<button type="button" class="klump-modal__close" aria-label="Close">&times;</button>' +
        '<div class="klump-modal__head">' +
          '<div class="klump-modal__title">Buy Now, Pay Later</div>' +
          '<div class="klump-modal__sub">Split your order into easy payments with Klump. Enter your details and choose delivery.</div>' +
        '</div>' +
        '<form class="klump-modal__form" novalidate>' +
          '<div class="klump-grid">' +
            '<label class="klump-field"><span>First name</span>' +
            '<input name="firstName" type="text" autocomplete="given-name" minlength="2" required></label>' +
            '<label class="klump-field"><span>Last name</span>' +
            '<input name="lastName" type="text" autocomplete="family-name" minlength="2" required></label>' +
            '<label class="klump-field"><span>Email</span>' +
            '<input name="email" type="email" autocomplete="email" required></label>' +
            '<label class="klump-field"><span>Phone</span>' +
            '<input name="phone" type="tel" autocomplete="tel" required></label>' +
            addressFields +
          '</div>' +
          deliverySection +
          '<div class="klump-modal__summary">' +
            '<div class="klump-sumrow"><span>Subtotal</span><strong class="klump-subtotal">—</strong></div>' +
            '<div class="klump-sumrow"><span>Delivery</span><strong class="klump-shipping">—</strong></div>' +
            '<div class="klump-sumrow klump-sumrow--total"><span>Total</span><strong class="klump-total">—</strong></div>' +
          '</div>' +
          '<button type="submit" class="klump-modal__submit" disabled>Continue to Klump</button>' +
          '<p class="klump-modal__note">You\'ll complete payment securely in the Klump window. Prices are confirmed by our store.</p>' +
        '</form>' +
      '</div>';

    document.body.appendChild(overlay);

    overlay.querySelector('.klump-modal__close').addEventListener('click', closeModal);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeModal(); });
    overlay.querySelector('form').addEventListener('submit', onSubmit);

    var ratesBtn = overlay.querySelector('.klump-rates-btn');
    if (ratesBtn) ratesBtn.addEventListener('click', onGetRates);

    // Changing the address invalidates any fetched rates.
    ['address1', 'city', 'province'].forEach(function (n) {
      var el = overlay.querySelector('[name="' + n + '"]');
      if (el) el.addEventListener('change', resetRates);
    });

    modal = overlay;
    return modal;
  }

  function openModal(cart, isProduct) {
    buildModal();
    modal.__cart = cart;
    modal.__isProduct = !!isProduct;
    modal.__selectedHandle = null;
    modal.querySelector('.klump-subtotal').textContent = fmt(cart.total_price / 100);
    modal.querySelector('.klump-shipping').textContent = '—';
    modal.querySelector('.klump-total').textContent = fmt(cart.total_price / 100);
    resetRates();
    // With no address step, allow submit straight away.
    if (!COLLECT_ADDRESS) modal.querySelector('.klump-modal__submit').disabled = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    var first = modal.querySelector('input[name="firstName"]');
    if (first) setTimeout(function () { first.focus(); }, 50);
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }

  function resetRates() {
    if (!modal) return;
    modal.__selectedHandle = null;
    var box = modal.querySelector('.klump-rates');
    if (box) { box.hidden = true; box.innerHTML = ''; }
    var btn = modal.querySelector('.klump-rates-btn');
    if (btn) { btn.hidden = false; btn.disabled = false; }
    var ship = modal.querySelector('.klump-shipping');
    if (ship) ship.textContent = '—';
    if (COLLECT_ADDRESS) modal.querySelector('.klump-modal__submit').disabled = true;
    updateTotal();
  }

  function updateTotal() {
    if (!modal || !modal.__cart) return;
    var base = modal.__cart.total_price / 100;
    var ship = 0;
    var rate = selectedRate();
    if (rate) ship = rate.price;
    modal.querySelector('.klump-total').textContent = fmt(base + ship);
  }

  function selectedRate() {
    if (!modal || !modal.__rates || !modal.__selectedHandle) return null;
    return modal.__rates.filter(function (r) { return r.handle === modal.__selectedHandle; })[0] || null;
  }

  function readForm(form) {
    var d = {};
    ['firstName', 'lastName', 'email', 'phone', 'address1', 'address2', 'city', 'province'].forEach(function (k) {
      var el = form.elements[k];
      d[k] = el ? String(el.value || '').trim() : '';
    });
    d.phone = normalizePhone(d.phone);
    return d;
  }

  function validateFields(form, names) {
    for (var i = 0; i < names.length; i++) {
      var el = form.elements[names[i]];
      if (el && !el.checkValidity()) { el.reportValidity(); return false; }
    }
    return true;
  }

  // ---- shipping rates -----------------------------------------------------
  function onGetRates() {
    var form = modal.querySelector('form');
    var needed = ['firstName', 'lastName', 'email', 'phone', 'address1', 'city', 'province'];
    if (!validateFields(form, needed)) return;

    var data = readForm(form);
    var cart = modal.__cart;
    var btn = modal.querySelector('.klump-rates-btn');
    btn.disabled = true;
    btn.textContent = 'Checking delivery options…';

    var payload = {
      items: cart.items.map(function (i) { return { variantId: i.variant_id, quantity: i.quantity }; }),
      customer: { firstName: data.firstName, lastName: data.lastName, email: data.email, phone: data.phone },
      shipping: { address1: data.address1, address2: data.address2, city: data.city, provinceCode: data.province, country: 'Nigeria' }
    };

    fetch(BACKEND + '/api/klump/shipping-rates', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    })
      .then(function (res) { return res.json().then(function (j) { if (!res.ok) throw new Error(j.error || 'Could not load delivery options.'); return j; }); })
      .then(function (j) { renderRates(j.rates || []); })
      .catch(function (err) {
        console.error('[Klump]', err);
        toast(err.message || 'Could not load delivery options.', true);
        btn.disabled = false;
        btn.textContent = 'Get delivery options';
      });
  }

  function renderRates(rates) {
    modal.__rates = rates;
    var box = modal.querySelector('.klump-rates');
    var btn = modal.querySelector('.klump-rates-btn');

    if (!rates.length) {
      box.hidden = true;
      toast('No delivery options for this address. Please check the details.', true);
      btn.disabled = false;
      btn.textContent = 'Get delivery options';
      return;
    }

    box.innerHTML =
      '<div class="klump-rates__label">Choose delivery</div>' +
      rates.map(function (r, idx) {
        return '<label class="klump-rate">' +
          '<input type="radio" name="klumpRate" value="' + r.handle + '"' + (idx === 0 ? ' checked' : '') + '>' +
          '<span class="klump-rate__title">' + r.title + '</span>' +
          '<span class="klump-rate__price">' + fmt(r.price) + '</span>' +
        '</label>';
      }).join('');
    box.hidden = false;
    btn.hidden = true;

    box.querySelectorAll('input[name="klumpRate"]').forEach(function (input) {
      input.addEventListener('change', function () {
        modal.__selectedHandle = input.value;
        modal.querySelector('.klump-shipping').textContent = fmt(rateByHandle(input.value).price);
        modal.querySelector('.klump-modal__submit').disabled = false;
        updateTotal();
      });
    });

    // Pre-select the first option.
    modal.__selectedHandle = rates[0].handle;
    modal.querySelector('.klump-shipping').textContent = fmt(rates[0].price);
    modal.querySelector('.klump-modal__submit').disabled = false;
    updateTotal();
  }

  function rateByHandle(h) {
    return (modal.__rates || []).filter(function (r) { return r.handle === h; })[0] || { price: 0 };
  }

  // ---- checkout -----------------------------------------------------------
  function onSubmit(e) {
    e.preventDefault();
    if (busy) return;
    var form = e.currentTarget;
    if (!form.checkValidity()) { form.reportValidity(); return; }
    if (COLLECT_ADDRESS && !modal.__selectedHandle) {
      toast('Please choose a delivery option.', true);
      return;
    }

    var cart = modal.__cart;
    var data = readForm(form);
    var submit = form.querySelector('.klump-modal__submit');

    busy = true;
    submit.disabled = true;
    submit.textContent = 'Preparing your order…';

    startCheckout(cart, data, modal.__selectedHandle)
      .catch(function (err) {
        console.error('[Klump]', err);
        toast(err && err.message ? err.message : 'Could not start Klump checkout. Please try again.', true);
      })
      .then(function () {
        busy = false;
        submit.disabled = false;
        submit.textContent = 'Continue to Klump';
      });
  }

  function startCheckout(cart, form, shippingRateHandle) {
    // The SDK exposes `Klump` as a bare global (class Klump {}), not window.Klump.
    // typeof on an undeclared identifier is safe (returns 'undefined', no throw).
    if (typeof Klump === 'undefined') {
      return Promise.reject(new Error('Klump is still loading. Please try again in a moment.'));
    }

    function gfCk(n) {
      var m = document.cookie.match('(?:^|; )' + n + '=([^;]*)');
      return m ? decodeURIComponent(m[1]) : null;
    }
    var payload = {
      items: cart.items.map(function (i) {
        return {
          variantId: i.variant_id,
          productId: i.product_id,
          quantity: i.quantity,
          unitPrice: i.final_price != null ? i.final_price / 100 : null,
          title: i.product_title || i.title
        };
      }),
      customer: {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone
      },
      shipping: {
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        provinceCode: form.province,
        country: 'Nigeria'
      },
      shippingRateHandle: shippingRateHandle || null,
      shippingRateTitle: (selectedRate() && selectedRate().title) || null,
      analytics: {
        sessionId: gfCk('_shopify_s') || gfCk('gf_sid'),
        visitorId: gfCk('_shopify_y') || gfCk('gf_vid'),
        customerId: (window.GF_ANALYTICS && window.GF_ANALYTICS.customerId != null) ? String(window.GF_ANALYTICS.customerId) : null
      }
    };

    return fetch(BACKEND + '/api/klump/create-draft', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res.json().then(function (j) {
          if (!res.ok) throw new Error(j.error || 'Could not start Klump checkout.');
          return j;
        });
      })
      .then(function (draft) {
        return getPublicKey().then(function (pk) {
          if (!pk) throw new Error('Klump is not configured. Please contact support.');

          var items = cart.items.map(function (i) {
            var name = (i.product_title || i.title || 'Item');
            if (i.variant_title && i.variant_title !== 'Default Title') name += ' - ' + i.variant_title;
            var item = {
              item_url: window.location.origin + (i.url || '/').split('?')[0],
              name: String(name).slice(0, 120),
              unit_price: Math.round(i.final_price / 100),
              quantity: i.quantity
            };
            var img = toAbsoluteUrl(i.image);
            if (img) item.image_url = img; // omit entirely when there's no valid image
            return item;
          });

          var rate = selectedRate();
          ensureCheckoutHost();

          /* global Klump */
          new Klump({
            publicKey: pk,
            data: {
              amount: Number(draft.amount),
              currency: draft.currency || 'NGN',
              merchant_reference: draft.reference,
              first_name: form.firstName,
              last_name: form.lastName,
              email: form.email,
              phone: form.phone,
              shipping_fee: rate ? Number(rate.price) : 0,
              meta_data: { source: 'getfit-cart', reference: draft.reference },
              items: items
            },
            onLoad: function () {},
            onOpen: function () { closeModal(); },
            onError: function (err) {
              console.error('[Klump] popup error', err);
              toast('Payment could not be completed.', true);
            },
            onClose: function () { pollStatus(draft.reference, false, modal.__isProduct); },
            onSuccess: function (res) {
              console.log('[Klump] success', res);
              toast('Payment received — finalising your order…');
              pollStatus(draft.reference, true, modal.__isProduct);
            }
          });
        });
      });
  }

  // Poll the backend until its webhook has completed the order.
  function pollStatus(reference, expectPaid, isProduct) {
    var deadline = Date.now() + 90000; // up to 90s
    var interval = expectPaid ? 2500 : 4000;

    function finish(j) {
      if (j.orderStatusUrl) {
        window.location.href = j.orderStatusUrl;
      } else {
        toast('Order confirmed! Check your email for details.');
      }
    }

    (function tick() {
      fetch(BACKEND + '/api/klump/status/' + encodeURIComponent(reference))
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (j.status === 'paid') {
            // Whole-cart purchase: clear the cart. Single-product: leave the
            // shopper's cart untouched (they didn't buy it).
            if (isProduct) {
              finish(j);
            } else {
              fetch('/cart/clear.js', { method: 'POST' }).catch(function () {}).then(function () { finish(j); });
            }
            return;
          }
          if (Date.now() < deadline) setTimeout(tick, interval);
        })
        .catch(function () {
          if (Date.now() < deadline) setTimeout(tick, interval);
        });
    })();
  }

  // ---- entry point --------------------------------------------------------
  function onBuyClick(btn) {
    if (busy) return;
    if (!BACKEND) { toast('Klump is not configured yet.', true); return; }

    // Product-page button => just this item; cart button => the whole cart.
    var isProduct = btn.hasAttribute('data-klump-product');
    var getter = isProduct ? getProductSelection(btn) : getCart();
    getter.then(function (cart) {
      if (!cart || !cart.item_count) { toast('Your cart is empty.', true); return; }
      var naira = cart.total_price / 100;
      if (MIN_AMOUNT > 0 && naira < MIN_AMOUNT) {
        toast('Klump is available on orders over ' + fmt(MIN_AMOUNT) + '.', true);
        return;
      }
      openModal(cart, isProduct);
    }).catch(function (err) {
      console.error('[Klump]', err);
      toast('Could not start Klump. Please try again.', true);
    });
  }

  // Delegated so it works for cart-drawer buttons that get re-rendered.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('.klump-buy-btn') : null;
    if (!btn) return;
    e.preventDefault();
    onBuyClick(btn);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeModal();
  });
})();
